import { useState } from 'react';
import Modal from '../ui/Modal';
import { newId, termForDate } from '../../data/model';

const NEW_FOLDER = '__new__';

function AddEventDialog({ folders, terms = [], activeTermId, defaultFolderId = '', dispatch, onAdded, onClose }) {
  const [form, setForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    weight: '1',
    // The folder on screen, when the view is one folder; otherwise none, as v1
    // did. Guessing any other folder files events somewhere the user did not
    // choose — invisibly, if it is collapsed.
    folderId: folders.some((folder) => folder.id === defaultFolderId) ? defaultFolderId : '',
    newFolderName: '',
    termId: terms.some((term) => term.id === activeTermId) ? activeTermId : '',
  });
  const [termTouched, setTermTouched] = useState(false);
  // Chosen once per dialog rather than per submit, so a second press of Add
  // before the dialog has closed names the same event and is dropped.
  const [ids] = useState(() => ({ event: newId('e'), folder: newId('f') }));

  const set = (changes) => setForm((current) => ({ ...current, ...changes }));
  const creatingFolder = form.folderId === NEW_FOLDER;
  const canSubmit = form.name.trim() && (!creatingFolder || form.newFolderName.trim());

  // The date says which semester an event belongs to better than whichever
  // term happens to be open, unless someone has picked one by hand.
  const setDate = (startDate) => {
    const dated = termTouched ? null : termForDate(terms, startDate);
    set(dated ? { startDate, termId: dated.id } : { startDate });
  };

  const submit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    const id = ids.event;
    const newFolderId = creatingFolder ? ids.folder : null;
    const folderId = creatingFolder ? null : form.folderId || null;
    dispatch({
      type: 'events/add',
      id,
      name: form.name.trim(),
      weight: form.weight,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      folderId,
      newFolderId,
      newFolderName: creatingFolder ? form.newFolderName.trim() : null,
      termId: form.termId || null,
    });
    onAdded?.({
      eventId: id,
      folderId: newFolderId || folderId,
      newFolder: newFolderId ? { id: newFolderId, parentId: null } : null,
      termId: form.termId || null,
    });
    onClose();
  };

  return (
    <Modal
      title="Add event"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="add-event" className="btn btn--primary" disabled={!canSubmit}>
            Add event
          </button>
        </>
      }
    >
      <form id="add-event" onSubmit={submit} className="stack">
        <label className="field">
          <span>Name</span>
          <input
            className="input"
            autoFocus
            value={form.name}
            onChange={(event) => set({ name: event.target.value })}
          />
        </label>

        <div className="row">
          <label className="field">
            <span>Date</span>
            <input
              className="input"
              type="date"
              value={form.startDate}
              onChange={(event) => setDate(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Ends (optional)</span>
            <input
              className="input"
              type="date"
              min={form.startDate || undefined}
              value={form.endDate}
              onChange={(event) => set({ endDate: event.target.value })}
            />
          </label>
          <label className="field field--narrow">
            <span>Weight</span>
            <input
              className="input"
              type="number"
              min="0"
              step="0.5"
              value={form.weight}
              onChange={(event) => set({ weight: event.target.value })}
            />
          </label>
        </div>

        {terms.length > 0 && (
          <label className="field">
            <span>Term</span>
            <select
              className="select"
              value={form.termId}
              onChange={(event) => {
                setTermTouched(true);
                set({ termId: event.target.value });
              }}
            >
              <option value="">No term</option>
              {terms.map((term) => (
                <option key={term.id} value={term.id}>{term.name}</option>
              ))}
            </select>
          </label>
        )}

        <label className="field">
          <span>Folder</span>
          <select
            className="select"
            value={form.folderId}
            onChange={(event) => set({ folderId: event.target.value })}
          >
            <option value="">No folder</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>{folder.name}</option>
            ))}
            <option value={NEW_FOLDER}>New folder…</option>
          </select>
        </label>

        {creatingFolder && (
          <label className="field">
            <span>New folder name</span>
            <input
              className="input"
              autoFocus
              value={form.newFolderName}
              onChange={(event) => set({ newFolderName: event.target.value })}
            />
          </label>
        )}

        <p className="hint">
          Weight scales how much this event counts toward the weighted score. Leave it at 1 unless
          this event should matter more than the others.
        </p>
      </form>
    </Modal>
  );
}

export default AddEventDialog;
